import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, KeyRound, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";

const MIN_LEN = 8;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [invite, setInvite] = useState(searchParams.get("invite") === "1");
  const navigate = useNavigate();

  // validating | valid | invalid | done
  const [status, setStatus] = useState("validating");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    let active = true;
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then((r) => { if (active) { setStatus(r.data?.valid ? "valid" : "invalid"); if (r.data?.kind === "invite") setInvite(true); } })
      .catch(() => { if (active) setStatus("invalid"); });
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_LEN) {
      toast.error(`Password must be at least ${MIN_LEN} characters`);
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setStatus("done");
      toast.success("Password updated");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not reset your password. Please request a new link.";
      toast.error(detail);
      // Token likely expired/used — push the user toward requesting a fresh link.
      setStatus("invalid");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fc-hero-bg fc-paper-noise flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <div className="mb-6"><Wordmark withTagline /></div>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="w-full max-w-md">
        <Card className="border-border bg-card shadow-[0_18px_50px_rgba(20,20,20,0.10)]">
          <CardContent className="p-7">
            {status === "validating" && (
              <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground" data-testid="reset-password-validating">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <p className="text-sm">Checking your reset link&hellip;</p>
              </div>
            )}

            {status === "invalid" && (
              <div className="flex flex-col items-center gap-4 py-4 text-center" data-testid="reset-password-invalid">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-7 w-7 text-destructive" />
                </div>
                <h1 className="font-serif text-2xl font-semibold text-foreground">Link expired or invalid</h1>
                <p className="text-sm text-muted-foreground">
                  {invite
                    ? "This set-password link is no longer valid. Request a new one below using the email address we have on file for you."
                    : "This reset link is no longer valid. Reset links expire after 60 minutes and can only be used once."}
                </p>
                <div className="mt-2 flex w-full flex-col gap-2">
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="reset-password-request-new">
                    <Link to="/forgot-password">Request a new link</Link>
                  </Button>
                  <Button asChild variant="ghost" className="w-full">
                    <Link to="/login">Back to sign in</Link>
                  </Button>
                </div>
              </div>
            )}

            {status === "valid" && (
              <>
                <h1 className="font-serif text-2xl font-semibold text-foreground" data-testid="reset-password-heading">{invite ? "Welcome to the new site" : "Set a new password"}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {invite
                    ? "Your Friday Conversations profile came along with us. Choose a password to sign in — you’ll use the email this link was sent to."
                    : "Choose a strong password you don’t use anywhere else."}
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="password">{invite ? "Choose a password" : "New password"}</Label>
                    <div className="relative mt-1.5">
                      <Input id="password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={`At least ${MIN_LEN} characters`} className="pr-10" data-testid="reset-password-input" />
                      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none" aria-label={show ? "Hide password" : "Show password"} data-testid="reset-password-toggle-visibility">
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirm">Confirm password</Label>
                    <Input id="confirm" type={show ? "text" : "password"} required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter your new password" className="mt-1.5" data-testid="reset-password-confirm-input" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="reset-password-submit-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="mr-2 h-4 w-4" /> {invite ? "Set password & continue" : "Update password"}</>}
                  </Button>
                </form>
              </>
            )}

            {status === "done" && (
              <div className="flex flex-col items-center gap-4 py-4 text-center" data-testid="reset-password-done">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle2 className="h-7 w-7 text-primary" />
                </div>
                <h1 className="font-serif text-2xl font-semibold text-foreground">{invite ? "You’re all set" : "Password updated"}</h1>
                <p className="text-sm text-muted-foreground">{invite ? "Your password is saved. Sign in to pick up where you left off." : "You can now sign in with your new password."}</p>
                <Button className="mt-2 w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" onClick={() => navigate("/login", { replace: true })} data-testid="reset-password-go-to-login">
                  Go to sign in
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
